import React, { useState, useRef } from "react";
import { Loader2, X, Check, AlertTriangle, Mic, Sparkles } from "lucide-react";
import { authApi } from "./authApi";
import { API } from "./api";
import { SoundWave } from "./SoundWave";

// Import d'acquéreurs par dictée vocale — réutilisé en back-office et dans Mon compte.
export function VoiceImportModal({ onClose, onDone }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const [invite, setInvite] = useState("none");
  const [err, setErr] = useState("");
  const mrRef = useRef(null); const chunksRef = useRef([]); const timerRef = useRef(null);

  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 25 * 1024 * 1024) { setErr("Enregistrement trop long (max ~25 Mo)."); return; }
        setBusy(true);
        try { const fd = new FormData(); fd.append("audio", blob, "acq.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd, credentials: "include" });
          const d = await res.json(); setTranscript((t) => (t ? t + " " : "") + (d.text || ""));
        } catch (_) { setErr("Transcription impossible."); }
        setBusy(false);
      };
      mr.start(); mrRef.current = mr; setRecording(true); setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (_) { setErr("Micro indisponible."); }
  };
  const stop = () => { if (mrRef.current && recording) { mrRef.current.stop(); setRecording(false); } };
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const analyze = async () => {
    setBusy(true); setErr("");
    try { const d = await authApi.voiceParseAcq(transcript); setRows((d.acquereurs || []).map((a) => ({ ...a, _action: a._duplicate ? "update" : "create" }))); }
    catch (e) { setErr(e.message || "Analyse impossible."); }
    setBusy(false);
  };
  const upd = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const create = async () => {
    setBusy(true); setErr("");
    try { const d = await authApi.bulkCreateAcq(rows, invite);
      onDone(`${d.created} créé(s)${d.updated ? `, ${d.updated} mis à jour` : ""}${d.skipped ? `, ${d.skipped} ignoré(s)` : ""}.`); }
    catch (e) { setErr(e.message || "Création impossible."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm overflow-y-auto py-8 px-3" data-testid="voice-import-modal">
      <div className="max-w-3xl mx-auto bg-[#0d0d10] border border-white/10 p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-light text-white flex items-center gap-2"><Mic className="w-5 h-5 text-[#C19A6B]" /> Importer des acquéreurs par la voix</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!rows ? (
          <>
            <p className="text-sm text-zinc-400">Dictez vos contacts à la suite : « Monsieur Jean Dupont, 06…, email…, cherche une maison à Chantilly, budget 450 000 €, 3 chambres avec jardin. Ensuite Madame… ». Vous pouvez enregistrer plusieurs fois.</p>
            <div className="flex items-center gap-3">
              {!recording ? (
                <button onClick={start} disabled={busy} className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#A88358] text-white disabled:opacity-50 text-sm font-semibold uppercase tracking-wider px-4 py-2 rounded-[5px] transition-transform hover:scale-105 animate-pulse shadow-[0_0_16px_rgba(193,154,107,0.5)]" data-testid="vi-rec-start">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Transcription…</> : <><Mic className="w-4 h-4" /> Recherche vocale</>}</button>
              ) : (
                <button onClick={stop} className="inline-flex items-center gap-2 bg-red-500 text-white text-sm uppercase tracking-wider px-4 py-2 rounded-[5px]" data-testid="vi-rec-stop"><SoundWave className="text-white" bars={5} /> Arrêter · {fmt(secs)}</button>
              )}
            </div>
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder="La retranscription apparaît ici — vous pouvez aussi taper directement…" className="w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-zinc-200 min-h-[120px] outline-none focus:border-[#C19A6B]" data-testid="vi-transcript" />
            {err && <p className="text-sm text-red-400">{err}</p>}
            <button onClick={analyze} disabled={busy || transcript.trim().length < 15} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider py-3" data-testid="vi-analyze">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse IA…</> : <><Sparkles className="w-4 h-4" /> Analyser & découper</>}</button>
          </>
        ) : (
          <>
            <p className="text-sm text-zinc-300">{rows.length} acquéreur(s) détecté(s). Vérifiez et corrigez avant création.</p>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {rows.map((a, i) => (
                <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`vi-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">{(a.first_name || "") + " " + (a.last_name || "") || "Sans nom"}</span>
                    {a._duplicate && <span className="inline-flex items-center gap-1 text-[10px] text-amber-400"><AlertTriangle className="w-3 h-3" /> Doublon détecté</span>}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[["first_name", "Prénom"], ["last_name", "Nom"], ["phone", "Téléphone"], ["email", "Email"], ["city", "Ville"], ["budget_max", "Budget max"]].map(([k, lbl]) => (
                      <input key={k} value={a[k] || ""} onChange={(e) => upd(i, k, e.target.value)} placeholder={lbl} className="bg-white/5 border border-white/15 px-2 py-1.5 text-xs text-white outline-none focus:border-[#C19A6B]" />
                    ))}
                  </div>
                  {(a._missing || []).length > 0 && <p className="text-[10px] text-zinc-500">Manquant : {(a._missing).join(", ")}</p>}
                  <select value={a._action} onChange={(e) => upd(i, "_action", e.target.value)} className="bg-white/5 border border-white/15 px-2 py-1.5 text-xs text-white outline-none">
                    <option value="create" className="bg-[#0d0d10]">Créer une nouvelle fiche</option>
                    {a._duplicate && <option value="update" className="bg-[#0d0d10]">Mettre à jour l'existante</option>}
                    {a._duplicate && <option value="merge" className="bg-[#0d0d10]">Fusionner (compléter l'existante)</option>}
                    <option value="skip" className="bg-[#0d0d10]">Ignorer</option>
                  </select>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs text-zinc-400">Invitation OFF MARKETTE :</label>
              <select value={invite} onChange={(e) => setInvite(e.target.value)} className="bg-white/5 border border-white/15 px-2 py-1.5 text-xs text-white outline-none">
                <option value="none" className="bg-[#0d0d10]">Enregistrer sans inviter</option>
                <option value="now" className="bg-[#0d0d10]">Inviter maintenant</option>
                <option value="later" className="bg-[#0d0d10]">Programmer plus tard</option>
              </select>
            </div>
            {err && <p className="text-sm text-red-400">{err}</p>}
            <div className="flex gap-2">
              <button onClick={() => setRows(null)} className="flex-1 border border-white/15 text-zinc-300 text-sm uppercase tracking-wider py-2.5">Retour</button>
              <button onClick={create} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider py-2.5" data-testid="vi-create"><Check className="w-4 h-4" /> {busy ? "Création…" : "Créer les fiches"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
